import React from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";

type OpeningVariation = {
  name?: string | null;
  moves?: string | null;
};

type OpeningReference = {
  moves?: string | null;
  lessonSlug?: string | null;
  ecoCodes?: Array<string | null> | null;
  aliases?: Array<string | null> | null;
  side?: string | null;
  skillLevels?: Array<string | null> | null;
  character?: Array<string | null> | null;
  keyIdeas?: Array<string | null> | null;
  variations?: Array<OpeningVariation | null> | null;
};

function compact(values?: Array<string | null> | null) {
  return (
    values?.filter((value): value is string => Boolean(value?.trim())) || []
  );
}

function labelFromSlug(value: string) {
  return value
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export default function OpeningReferenceDetails({
  opening,
}: {
  opening?: OpeningReference | null;
}) {
  if (!opening) return null;

  const ecoCodes = compact(opening.ecoCodes);
  const aliases = compact(opening.aliases);
  const skillLevels = compact(opening.skillLevels);
  const character = compact(opening.character);
  const keyIdeas = compact(opening.keyIdeas);
  const variations =
    opening.variations?.filter((variation): variation is OpeningVariation =>
      Boolean(variation?.name),
    ) || [];
  const facts = [
    opening.moves && { label: "Starting moves", value: opening.moves },
    ecoCodes.length > 0 && { label: "ECO", value: ecoCodes.join(", ") },
    opening.side && { label: "Played by", value: labelFromSlug(opening.side) },
    skillLevels.length > 0 && {
      label: "Level",
      value: skillLevels.map(labelFromSlug).join(", "),
    },
    character.length > 0 && {
      label: "Character",
      value: character.map(labelFromSlug).join(", "),
    },
  ].filter(Boolean) as Array<{ label: string; value: string }>;

  if (
    facts.length === 0 &&
    aliases.length === 0 &&
    keyIdeas.length === 0 &&
    variations.length === 0 &&
    !opening.lessonSlug
  ) {
    return null;
  }

  return (
    <section
      aria-labelledby="opening-reference-heading"
      className="not-prose mb-10 overflow-hidden rounded-lg border border-athens-gray-200 bg-athens-gray-150"
    >
      <div className="border-b border-athens-gray-200 px-5 py-4 sm:px-6">
        <h2
          id="opening-reference-heading"
          className="text-sm font-bold uppercase tracking-[0.16em] text-slate-800"
        >
          Opening at a glance
        </h2>
      </div>

      {facts.length > 0 && (
        <dl className="grid gap-px bg-athens-gray-200 sm:grid-cols-2">
          {facts.map((fact) => (
            <div
              key={fact.label}
              className="bg-athens-gray-150 px-5 py-4 sm:px-6"
            >
              <dt className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                {fact.label}
              </dt>
              <dd className="mt-1 text-sm font-semibold leading-6 text-slate-900">
                {fact.value}
              </dd>
            </div>
          ))}
          {facts.length % 2 === 1 && (
            <div
              className="hidden bg-athens-gray-150 sm:block"
              aria-hidden="true"
            />
          )}
        </dl>
      )}

      {(aliases.length > 0 || keyIdeas.length > 0 || variations.length > 0) && (
        <div className="grid gap-6 border-t border-athens-gray-200 px-5 py-5 sm:grid-cols-2 sm:px-6">
          {aliases.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-900">
                Also known as
              </h3>
              <p className="mt-2 text-sm leading-6 text-slate-700">
                {aliases.join(", ")}
              </p>
            </div>
          )}
          {keyIdeas.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-900">
                Key ideas
              </h3>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-6 text-slate-700">
                {keyIdeas.map((idea) => (
                  <li key={idea}>{idea}</li>
                ))}
              </ul>
            </div>
          )}
          {variations.length > 0 && (
            <div className="sm:col-span-2">
              <h3 className="text-sm font-semibold text-slate-900">
                Main variations
              </h3>
              <ul className="mt-2 divide-y divide-athens-gray-200 border-y border-athens-gray-200">
                {variations.map((variation) => (
                  <li
                    key={`${variation.name}-${variation.moves || ""}`}
                    className="py-2.5 text-sm"
                  >
                    <span className="font-semibold text-slate-800">
                      {variation.name}
                    </span>
                    {variation.moves && (
                      <span className="mt-0.5 block text-slate-600">
                        {variation.moves}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {opening.lessonSlug && (
        <div className="border-t border-athens-gray-200 px-5 py-4 sm:px-6">
          <Button asChild variant="default" size="default">
            <Link href={`/learn/openings/${opening.lessonSlug}`} locale={false}>
              Practice this opening
              <span className="ml-2" aria-hidden="true">
                →
              </span>
            </Link>
          </Button>
        </div>
      )}
    </section>
  );
}
